/**
 * Referral capture for the affiliate program.
 *
 * A visitor arriving on ?ref=CODE is remembered locally until they have an
 * account to attach the referral to — attribution needs a profile, and that
 * doesn't exist until they register. First code wins, matching the server's
 * first-touch rule, so a later link can't overwrite who actually brought them.
 */
const REF_KEY = "memnora-ref";
const REF_SENT_KEY = "memnora-ref-sent";

export function captureReferralFromUrl(): void {
  try {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (!code) return;
    const clean = code.trim().toUpperCase().slice(0, 24);
    if (!/^[A-Z2-9]{4,24}$/.test(clean)) return;
    if (localStorage.getItem(REF_KEY)) return; // first touch wins
    localStorage.setItem(REF_KEY, clean);
  } catch {
    // Storage disabled — the referral simply isn't tracked.
  }
}

/**
 * Send a captured referral once the visitor has an account. No-ops when there
 * is nothing to send or it has already been sent, so it is safe to call on
 * every render pass.
 */
export async function flushReferral(): Promise<void> {
  try {
    const code = localStorage.getItem(REF_KEY);
    if (!code || localStorage.getItem(REF_SENT_KEY) === "1") return;

    const res = await fetch("/api/billing/affiliate/attribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code }),
    });
    // 401 means they aren't signed in yet — keep the code and try again later.
    if (res.status === 401) return;
    // Any settled answer (attributed or a stale code) is final; don't retry.
    localStorage.setItem(REF_SENT_KEY, "1");
  } catch {
    // Network hiccup — it'll be retried on the next load.
  }
}
